import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { loadStripe, StripeCheckoutOptions } from "@stripe/stripe-js"
import {
  PaymentElement,
  useStripe,
  CheckoutProvider,
  AddressElement,
  useCheckout,
} from "@stripe/react-stripe-js"
import { useStripePaymentMutation } from "../mutations"
import * as colors from "../components/colors"
import { Button } from "../components/Button"
import { Spin } from "antd"
import { styled } from "styled-components"
import { Plan } from './PurchaseFlow';
import currency from 'currency.js';
import { yellow } from '../components/colors';
import { TextInput } from '../ui/TextInput';

const PriceBoxWrapper = styled.div`
    padding: 16px;
    border-radius: 16px;
    border: 2px solid ${colors.yellow};
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const PriceBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.2rem;
    
    &.discount {
        color: ${colors.red};
        font-weight: 600;
    }
    
    &.total {
        font-weight: 600;
        color: ${colors.yellow};
        font-size: 1.4rem;
        border-top: 1px solid rgba(255,255,255, 0.2);
        padding-top: 8px;
    }
    .price {
        font-weight: 600;
    }
`

const CheckoutForm: FC<{ onReady: () => void, onProgress: (isLoading: boolean) => void, selectedPlan?: Plan }> = ({ onReady, onProgress, selectedPlan }) => {
  const stripe = useStripe()
  const checkout = useCheckout()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { canConfirm } = checkout || {}
  const [addressLoaded, setAddressLoaded] = useState(false)
  const [paymentLoaded, setPaymentLoaded] = useState(false)
  const [prices, setPrices] = useState<{ total: string | null, tax: string | null, discount: string | null, subTotal: string | null } | null>(null)

  const initLoading = useMemo(() => {
    return !addressLoaded || !paymentLoaded
  }, [addressLoaded, paymentLoaded])

  useEffect(() => {
    if (!initLoading) {
      onReady?.()
    }
  }, [initLoading])

  useEffect(() => {
    if (checkout) {
      setPrices({
        total: checkout.total.total.amount,
        tax: checkout.total.taxExclusive.amount,
        discount: checkout.total.discount?.minorUnitsAmount > 0 ? checkout.total.discount.amount : null,
        subTotal: checkout.total.subtotal.amount
      })
    }
    console.log('checkout', JSON.parse(JSON.stringify(checkout)))
  }, [checkout])

  const handleSubmit = async e => {
    if (e) {
      e.preventDefault()
    }
    setIsLoading(true)
    onProgress(true)

    if (checkout === null || !stripe) {
      return
    }

    try {
      const confirmResult = await checkout.confirm()
      if (confirmResult.type === "error") {
        setErrorMessage(confirmResult.error.message)
      }
    } catch (error) {
      console.error("Error confirming payment:", error)
      setErrorMessage(
        ((error as unknown) as Error).message ||
          "An error occurred while processing your payment."
      )
    } finally {
      setIsLoading(false)
      onProgress(false)
    }
  }

  const [discountCode, setDiscountCode] = useState<string>("")
  const [showCouponForm, setShowCouponForm] = useState<boolean>(false)
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null)
  const [couponError, setCouponError] = useState<boolean>(false)

  const onApplyCoupon = useCallback(() => {
    setCouponError(false)
    checkout.applyPromotionCode(discountCode).then(
      (data) => {
        console.log('discount applied', data)
        if (data.type === 'success') {
          setAppliedCoupon(discountCode);
          setDiscountCode('');
          setShowCouponForm(false);
        } else {
          setCouponError(true);
          setDiscountCode('');
        }
      },
      (error) => {
        console.error('discount failed', error)
        setCouponError(true);
        setDiscountCode('');
      }
    )
  }, [discountCode])

  return (
    <>
      <div style={{ display: initLoading ? 'none' : 'block' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <h3 style={{ marginTop: 0 }}>Billing address</h3>
          <AddressElement
            onReady={() => setAddressLoaded(true)}
            options={{ mode: 'billing' }}
          />
          <h3>Payment method</h3>
          <PaymentElement onReady={() => setPaymentLoaded(true)} />
          {errorMessage && <div>{errorMessage}</div>}
          {checkout && (
            <>
              {showCouponForm ? (
                <div>
                  <div
                    style={{
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      gap: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ width: '100%', maxWidth: '300px' }}>
                      <TextInput
                        value={discountCode}
                        placeholder={'Type your discount code'}
                        onChange={setDiscountCode}
                      />
                    </div>
                    <Button onClick={onApplyCoupon} large={true}>
                      Apply
                    </Button>
                  </div>
                  <div>
                    {couponError && (
                      <div style={{ color: colors.red, fontSize: '16px' }}>
                        Invalid discount code.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    paddingTop: '26px',
                    paddingBottom: '10px',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      paddingBottom: '10px',
                      fontSize: '18px',
                      fontWeight: 600,
                    }}
                  >
                    Do you have a discount code?
                  </div>
                  <Button
                    disabled={appliedCoupon !== null}
                    onClick={() => setShowCouponForm(true)}
                    large={false}
                  >
                    Apply Coupon
                  </Button>
                </div>
              )}
              <PriceBoxWrapper style={{ marginTop: '24px' }}>
                <PriceBox>
                  <div>Package price</div>
                  <div className={'price'}>{prices?.subTotal}</div>
                </PriceBox>
                {prices?.discount && (
                  <>
                    <PriceBox className={'discount'}>
                      <div>Discount</div>
                      <div className={'price'}>{prices?.discount}</div>
                    </PriceBox>
                    {appliedCoupon && (
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 600 }}>
                          Coupon applied: {appliedCoupon}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <PriceBox>
                  <div>Tax</div>
                  <div className={'price'}>{prices?.tax}</div>
                </PriceBox>
                <PriceBox className={'total'}>
                  <div>Total</div>
                  <div className={'price'}>{prices?.total}</div>
                </PriceBox>
                {selectedPlan && selectedPlan.id === 'basic' && (
                  <div
                    style={{
                      borderTop: '1px solid rgba(255,255,255, 0.2)',
                      paddingTop: '8px',
                    }}
                  >
                    <p style={{ fontSize: '16px', margin: 0, fontWeight: 600 }}>
                      Recurring PRO plan after{' '}
                      <span style={{ color: yellow }}>2 weeks trial</span>{' '}
                      period. You can cancel it at any time.
                      <div>
                        Your selected plan:{' '}
                        <span style={{ color: yellow }}>
                          {currency(selectedPlan?.stripePackage?.amount, {
                            fromCents: true,
                            symbol:
                              selectedPlan?.stripePackage?.currency === 'eur'
                                ? '€'
                                : '$',
                          }).format()}
                          +VAT /
                          {selectedPlan?.stripePackage?.interval === 'year'
                            ? 'year'
                            : 'month'}
                        </span>
                      </div>
                    </p>
                  </div>
                )}
                {selectedPlan && selectedPlan.id === 'pro' && (
                  <div
                    style={{
                      borderTop: '1px solid rgba(255,255,255, 0.2)',
                      paddingTop: '8px',
                    }}
                  >
                    <p style={{ fontSize: '16px', margin: 0, fontWeight: 600 }}>
                      Your selected plan:{' '}
                      <span style={{ color: yellow }}>
                        {currency(selectedPlan?.stripePackage?.amount, {
                          fromCents: true,
                          symbol:
                            selectedPlan?.stripePackage?.currency === 'eur'
                              ? '€'
                              : '$',
                        }).format()}
                        +VAT /
                        {selectedPlan?.stripePackage?.interval === 'year'
                          ? 'year'
                          : 'month'}
                      </span>
                    </p>
                  </div>
                )}
              </PriceBoxWrapper>
            </>
          )}
          <div style={{ marginTop: '16px' }}>
            <Button
              buttonType={
                !stripe || isLoading || !canConfirm ? 'button' : 'submit'
              }
              large={true}
              disabled={!stripe || isLoading || !canConfirm}
            >
              Submit Purchase
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

export const StripeCheckout: React.FC<{
  onCancelPaymentProcess: () => void
  planId: string
  billingPeriod: "month" | "year",
  selectedPlan?: Plan
}> = ({ onCancelPaymentProcess, planId, billingPeriod, selectedPlan }) => {
  const { mutateAsync: fetchClientSecret } = useStripePaymentMutation({
    planId,
    billingPeriod: billingPeriod === 'year' ? "yearly" : billingPeriod === 'month' ? "monthly" : "monthly",
    returnUrl: `${window.location.origin}/purchase-complete?session_id={CHECKOUT_SESSION_ID}`
  })

  const [isReady, setIsReady] = useState(false)
  const [isCancelDisabled, setCancelDisabled] = useState(false)

  const stripe = useMemo(() => {
    if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
      return null
    }
    return loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY, {
      locale: "en"
    })
  }, [])

  if (!stripe) {
    return <></>
  }

  const options: StripeCheckoutOptions = {
    fetchClientSecret,
    elementsOptions: {
      loader: "always",
      fonts: [
        {
          cssSrc:
            "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Space+Grotesk:wght@300..700&display=swap"
        }
      ],
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: colors.yellow,
          colorText: colors.white,
          colorBackground: colors.darkBlueBackground,
          colorDanger: colors.red,
          fontFamily: "Space Grotesk, system-ui, sans-serif",
          borderRadius: "12px"
        }
      }
    }
  }

  return (
    <div style={{ minWidth: "400px" }}>
      {!isReady && (
        <div>
          <div>
            <Spin size={"large"} />
          </div>
          <p>Loading payment options...</p>
          <p style={{ fontSize: "16px" }}>
            Please wait while we prepare the payment.
          </p>
        </div>
      )}
      <CheckoutProvider stripe={stripe} options={options}>
        <CheckoutForm
          onReady={() => setIsReady(true)}
          onProgress={(isLoading) => setCancelDisabled(isLoading)}
          selectedPlan={selectedPlan}
        />
        {isReady && (
          <Button
            secondary={true}
            large={true}
            disabled={isCancelDisabled}
            onClick={onCancelPaymentProcess}
          >
            Cancel
          </Button>
        )}
      </CheckoutProvider>
    </div>
  )
}
